import AvaliacaoOpcaoResposta from './AvaliacaoOpcaoResposta';

/** Importando tipos utilizados na documentação 
 * @typedef {import('./QuestionarioOpcaoResposta').default} QuestionarioOpcaoResposta
 */

/**
 * Objeto que representa uma questão do questionário
 */
class AvaliacaoResposta {

    /**
     * Cria uma nova instância de uma questão
     * Caso os parâmetros não sejam informados, cria com as propriedades vazias
     * 
     * @param {Number|null} id Id da questão na API
     * @param {String|null} titulo Título da questão
     * @param {Boolean|null} obrigatoria Indica se o preenchimento da questão é obrigatório
     * @param {String|null} tipo Tipo da questão
     * @param {Number|null} ordem Ordem da questão na seção
     * @param {Boolean|null} opcaoOutros Indica se a questão irá ter uma opção "Outros" no preenchimento
     * @param {Boolean|null} exibeGrafico Indica se a questão irá ser exibida como gráfico na impressão
     * @param {Boolean|null} limitaValor Indica se a questão irá limitar o valor da resposta
     * @param {Number|null} valorMinimo Valor mínimo possível para a resposta
     * @param {Number|null} valorMaximo Valor máximo possível para a resposta
     * @param {String|null} conteudoTexto Conteúdo fixo para ser exibido na avaliação
     * @param {Boolean|null} exibeResultado Indica se a questão deve ser exibida na avaliação ou no resultado
     * @param {Array<QuestionarioOpcaoResposta>|null} opcoes Opções de resposta para a questão
     * @param {String|Number|Boolean|AvaliacaoOpcaoResposta|Array<AvaliacaoOpcaoResposta>|null} resposta Resposta preenchida pelo usuário
     * @param {String|null} outro Opção preenchida quando o usuário não seleciona nenhuma das opções disponíveis
     * @param {String|null} novaImagem Nova imagem selecionada pelo usuário
     */
    constructor (id, titulo, obrigatoria, tipo, ordem, opcaoOutros, exibeGrafico, limitaValor, valorMinimo, valorMaximo, conteudoTexto, exibeResultado, opcoes, resposta, outro, novaImagem) {
        /** @member {number} id Id da questão na API */
        this.id = id ?? null;
        /** @member {string} titulo Título da questão */
        this.titulo = titulo ?? '';
        /** @member {boolean} obrigatoria Indica se o preenchimento da questão é obrigatório */
        this.obrigatoria = obrigatoria ?? false;
        /** @member {String|null} tipo Tipo da questão */
        this.tipo = tipo ?? '';
        /** @member {boolean} ordem Ordem da seção no questionário */
        this.ordem = ordem ?? 1;
        /** @member {Boolean|null} opcaoOutros Indica se a questão irá ter uma opção "Outros" no preenchimento */
        this.opcaoOutros = opcaoOutros ?? false;
        /** @member {Boolean|null} exibeGrafico Indica se a questão irá ser exibida como gráfico na impressão */
        this.exibeGrafico = exibeGrafico ?? false;
        /** @member {Boolean|null} limitaValor Indica se a questão irá limitar o valor da resposta */
        this.limitaValor = limitaValor ?? false;
        /** @member {Number|null} valorMinimo Valor mínimo possível para a resposta */
        this.valorMinimo = valorMinimo ?? 0;
        /** @member {Number|null} valorMaximo Valor máximo possível para a resposta */
        this.valorMaximo = valorMaximo ?? 0;
        /** @member {String|null} conteudoTexto Conteúdo fixo para ser exibido na avaliação */
        this.conteudoTexto = conteudoTexto;
        /** @member {Boolean|null} exibeResultado Indica se a questão deve ser exibida na avaliação ou no resultado */
        this.exibeResultado = exibeResultado;
        /** @member {Array<QuestionarioOpcaoResposta>|null} opcoes Opções de resposta para a questão */
        this.opcoes = opcoes ?? [];
        /** @member {String|Number|Boolean|Array<Number>|null} resposta Resposta preenchida pelo usuário */
        this.resposta = resposta ?? null;
        /** @member {String|null} outro Opção preenchida quando o usuário não seleciona nenhuma das opções disponíveis */
        this.outro = outro ?? null;
        /** @member {String|null} novaImagem Nova imagem selecionada pelo usuário */
        this.novaImagem = novaImagem ?? null;
    }

    /**
     * Cria uma cópia do objeto
     * @param {AvaliacaoResposta} origem Questão a ser clonada
     * @returns {AvaliacaoResposta} Novo objeto com os mesmos valores que o original
     */
    static clone(origem) {
        let opcoes = null;
        let resposta = null;
        
        if (origem.opcoes)
            opcoes = origem.opcoes.map(opcao => AvaliacaoOpcaoResposta.clone(opcao));
        
        //Identifica o tipo da propriedade para clonar
        if (origem.resposta instanceof Array)
            resposta = origem.resposta.slice();
        else
            resposta = origem.resposta;

        return new AvaliacaoResposta(
            origem.id, 
            origem.titulo, 
            origem.obrigatoria, 
            origem.tipo, 
            origem.ordem, 
            origem.opcaoOutros, 
            origem.exibeGrafico, 
            origem.limitaValor, 
            origem.valorMinimo, 
            origem.valorMaximo, 
            origem.conteudoTexto,
            origem.exibeResultado,
            opcoes, 
            resposta, 
            origem.outro, 
            origem.novaImagem);
    }
}

export default AvaliacaoResposta;